import React, { useEffect, useMemo } from "react";
import { useQuery, useMutation } from "react-query";
import { useParams } from "react-router";
import { get, startCase } from "lodash-es";

// Components
import {
  Wrapper,
  HeaderWrapper,
  Button,
  SectionCard,
  UploadLogoWrapper,
  Row,
  InputWrapper,
  Column,
} from "./ProductInfo.styles";
import { Popconfirm, notification, Space } from "antd";
import Input from "../components/Input";
import Switch from "../components/Switch";
import { HeaderText } from "../Header/Header.styles";
import DropdownSelect from "../components/DropdownSelect";
import UploadLogo from "../../../../components/UploadLogo";
import Insurance from "../components/Insurance";

import {
  // DownloadOutlined,
  EditOutlined,
  SaveOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { ConvertToVirtualKitButton } from "./ConvertToVirtualKit";
import { ConvertToBPButton } from "./ConvertToBaseProduct";

// Hooks & libs
import useProductCreateFlow from "../../../../hooks/reduxContainer/fulfillment/productCreateFlow";
import { useProductDetails } from "../../../../hooks/api/fulfillment/products";
import { fetchUserRole } from "api/accounts/roles";
import { objectToFormData } from "object-to-formdata";
import { syncProduct } from "../../../../api/fulfillment/product";

// Interfaces
import { PackagingLevel } from "interfaces/products";
import useUser from "hooks/reduxContainer/_common/useUser";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlagQuery";

const productTypeOptions = [
  { value: "base", text: "Base Product" },
  { value: "kit", text: "Virtual Kit" },
];

interface Props {
  isCreate?: boolean;
  isEditing?: boolean;
  name: string;
  image?: File | null;
  image_url?: string;
  product_type: string;
  active: boolean;
  description: string;
  scid: string;
  sku: string;
  upc: string;
  platform?: "manual" | "csv" | "shopify" | "api";
  under_conversion: boolean;
  setProduct?: () => void;
  isService?: boolean;
  onUpdateProduct?: () => void;
  insuranceRequired: boolean;
  insuranceValue: number | null;
  cost: number;
}

const ProductInfo: React.FC<Props> = ({
  isCreate,
  isEditing,
  name,
  image,
  image_url,
  product_type,
  active,
  description,
  scid,
  sku,
  upc,
  platform,
  under_conversion,
  setProduct,
  isService,
  onUpdateProduct,
  insuranceRequired,
  insuranceValue,
}) => {
  const {
    product,
    dispatchSetIsEditing,
    dispatchSetName,
    dispatchSetImage,
    dispatchSetImageURL,
    dispatchSetProductType,
    dispatchSetActive,
    dispatchSetDescription,
    dispatchSetSku,
    dispatchSetUPC,
    dispatchSetInsuranceRequired,
    dispatchSetInsuranceValue,
    dispatchSetIsService,
  } = useProductCreateFlow();
  const params = useParams<{ organization_id: string; product_id: string }>();
  const { productDetails, updateProduct } = useProductDetails();
  const {
    user: { organization_role_id: orgRoleID },
  } = useUser();

  const organizationId = get(params, "organization_id");

  const showServiceFeatures = useFeatureFlagQuery(
    "enable-product-service",
    {
      organization_id: organizationId ?? "",
    },
    { enabled: !!organizationId }
  );

  const role = useQuery(["fetchUserRole", orgRoleID, isEditing, product_type], () => fetchUserRole(orgRoleID), {
    enabled: !!orgRoleID && isEditing,
  });

  const { isProductTypeConversionEnabled, isProductResyncEnabled } = useMemo(() => {
    const isProductTypeConversionEnabled = !!role.data?.permissions.find(
      (permission) => permission.name === "manage_product_type_conversion"
    );
    const isProductResyncEnabled = !!role.data?.permissions.find(
      (permission) => permission.name === "manage_resync_product"
    );
    return { isProductTypeConversionEnabled, isProductResyncEnabled };
  }, [role.data]);

  const onRefetchProduct = () => {
    if (onUpdateProduct) onUpdateProduct();
    dispatchSetIsEditing(false);
  };

  const { mutateAsync: doSyncProduct } = useMutation(syncProduct, {
    onSuccess: onRefetchProduct,
  });

  useEffect(() => {
    if (productDetails.error) {
      notification.error({
        message: "Error",
        description: productDetails.error,
      });
    }
  }, [productDetails.error]);

  const handleUpdateProduct = () => {
    const updateProductBody: any = {
      ...product,
      images_attributes: image ? [{ file: image }] : null,
      packaging_levels_attributes: product.packaging_levels,
      manufacturing_country_origin: product.manufacturing_country_origin,
    };
    if (!image) delete updateProductBody.images_attributes;
    delete updateProductBody.image;
    delete updateProductBody.image_url;
    delete updateProductBody.packaging_levels;
    if (product_type === "kit") {
      delete updateProductBody.packaging_levels_attributes;
      delete updateProductBody.virtual_kits;
      delete updateProductBody.base_products;
    }
    if (product_type === "base") {
      delete updateProductBody.base_products;
      updateProductBody.packaging_levels_attributes = updateProductBody.packaging_levels_attributes
        .map((packingLevel: PackagingLevel) => {
          if (packingLevel.active) return packingLevel;
          if (!packingLevel.active && packingLevel?.id) return { id: packingLevel.id, _delete: true };
          return null;
        })
        .filter((packingLevel: PackagingLevel) => packingLevel) as (
        | PackagingLevel
        | { id: string; _delete: boolean }
      )[];
    }

    const requestBody = objectToFormData({ product: updateProductBody });
    updateProduct(product.id, requestBody).then(onRefetchProduct);
  };

  const renderInput = (
    label: string,
    required: boolean,
    defaultValue: string,
    handleChange: (value: string) => void,
    disabled?: boolean,
    width?: string
  ) => {
    return (
      <InputWrapper width={width}>
        <Input
          disabled={disabled || !isEditing}
          label={label}
          placeholder={required ? "Required" : "Optional"}
          required={required}
          defaultValue={defaultValue}
          handleChange={handleChange}
          displayOnly={!isEditing}
        />
      </InputWrapper>
    );
  };

  const validate = () => {
    if (!product_type || !name || !sku) return false;

    if (product_type === "base") {
      const { length, width, height, weight, cost, retail_price, wholesale_price } = product;

      if (
        length === undefined ||
        width === undefined ||
        height === undefined ||
        weight === undefined ||
        cost === undefined ||
        !isFinite(cost) ||
        retail_price === undefined ||
        !isFinite(retail_price) ||
        retail_price <= 0 ||
        cost < 0 ||
        wholesale_price < 0
      ) {
        return false;
      }
    }
    if (product_type === "kit") {
      const { base_products, required_parts } = product;
      const validBaseProducts = base_products?.every((base) => required_parts && base && required_parts[base.id] > 0);

      if (!base_products?.length || !validBaseProducts) return false;
    }

    return true;
  };

  const ableToSubmit = validate();
  const isBase = product_type === "base";

  const renderSyncProductButton = () => {
    if (!isProductResyncEnabled || platform !== "shopify" || !params?.product_id) return null;
    return (
      <Button margin="0 16px 0 0" onClick={() => doSyncProduct(params?.product_id)} icon={<SyncOutlined />}>
        Sync Product
      </Button>
    );
  };

  const renderVirtualKitButton = () => {
    if (!isProductTypeConversionEnabled || under_conversion || !product_type) return null;
    if (product_type === "base") {
      return (
        <ConvertToVirtualKitButton
          organizationId={organizationId}
          product={product}
          onRefetchProduct={onRefetchProduct}
        />
      );
    }
    // product is kit type
    return <ConvertToBPButton product={product} onRefetchProduct={onRefetchProduct} />;
  };

  const renderEditButtons = () =>
    isEditing ? (
      <Space>
        {renderSyncProductButton()}
        {renderVirtualKitButton()}
        <Popconfirm
          title="Are you sure?"
          onConfirm={() => {
            if (setProduct) setProduct();
            dispatchSetIsEditing(false);
          }}
          okText="Yes"
          cancelText="No"
        >
          <Button
            danger
            margin="0 16px 0 0"
            onClick={() => {
              dispatchSetIsEditing(false);
            }}
          >
            Cancel
          </Button>
        </Popconfirm>
        <Button
          disabled={!ableToSubmit}
          onClick={() => {
            handleUpdateProduct();
            dispatchSetIsEditing(false);
          }}
          icon={<SaveOutlined />}
        >
          Save
        </Button>
      </Space>
    ) : (
      <Button
        onClick={() => {
          dispatchSetIsEditing(true);
        }}
        icon={<EditOutlined />}
      >
        Edit Product
      </Button>
    );

  const renderFirstRow = () => (
    <Row>
      <Column flex={3}>
        {renderInput("Product Name", true, name, (value) => dispatchSetName(value), false, "80%")}
      </Column>
      <Column flex={3}>
        <InputWrapper minWidth={200}>
          <DropdownSelect
            label="Product Type"
            required
            value={product_type}
            options={productTypeOptions}
            handleChange={(value: string) => dispatchSetProductType(value as "base" | "kit")}
            disabled={!isCreate}
            displayOnly={!isCreate}
          />
        </InputWrapper>
      </Column>
      <Column flex={3}>
        <InputWrapper>
          <Switch
            disabled={!isEditing}
            checked={active}
            defaultChecked
            label="Status"
            checkedChildren="Active"
            unCheckedChildren="Archived"
            handleChange={(checked) => dispatchSetActive(checked)}
            displayOnly={!isEditing}
          />
        </InputWrapper>
      </Column>
      {isBase && showServiceFeatures.data?.state && (
        <Column flex={3}>
          {renderInput("UPC (Universal Product Code)", false, upc, (value) => dispatchSetUPC(value), false, "80%")}
        </Column>
      )}
    </Row>
  );

  const renderSecondRow = () => (
    <Row marginBottom={isEditing ? "16px" : "0"}>
      {!isCreate && (
        <Column flex={3}>{isCreate ? null : renderInput("SCID", false, scid, () => null, true, "80%")}</Column>
      )}
      <Column flex={3}>{renderInput("Product SKU", true, sku, (value) => dispatchSetSku(value), false, "80%")}</Column>
      {isBase && showServiceFeatures.data?.state ? (
        <Insurance
          isEditing={isEditing}
          insuranceRequired={insuranceRequired}
          insuranceValue={insuranceValue}
          handleInsuranceValueChange={dispatchSetInsuranceValue}
          handleInsuranceRequiredChange={dispatchSetInsuranceRequired}
          isIkeaOrg={showServiceFeatures.data?.state || false}
        />
      ) : (
        <Column flex={3}>
          {renderInput("UPC (Universal Product Code)", false, upc, (value) => dispatchSetUPC(value), false, "80%")}
        </Column>
      )}
    </Row>
  );

  const renderThirdRow = () => (
    <Row>
      <Column flex={!isCreate ? 3 : 9}>
        <InputWrapper width="80%">
          <Input
            textarea
            disabled={!isEditing}
            label="Description"
            placeholder="Optional"
            defaultValue={description}
            handleChange={(value) => dispatchSetDescription(value)}
            displayOnly={!isEditing}
          />
        </InputWrapper>
      </Column>
      {!isCreate && (
        <Column flex={3}>
          {platform ? (
            <InputWrapper>
              <Input displayOnly textarea label="Platform" defaultValue={startCase(platform)} />
            </InputWrapper>
          ) : null}
        </Column>
      )}
      <Column flex={isBase && showServiceFeatures.data?.state ? 6 : 3}>
        {showServiceFeatures.data?.state && (
          <InputWrapper minWidth={200}>
            <Switch
              disabled={!isEditing}
              checked={product.is_service ?? isService}
              label="Service Product"
              checkedChildren="On"
              unCheckedChildren="Off"
              handleChange={dispatchSetIsService}
              displayOnly={!isEditing}
              displayOnlyLabels={["Yes", "No"]}
            />
          </InputWrapper>
        )}
        {!showServiceFeatures.data?.state && isBase && (
          <Insurance
            isEditing={isEditing}
            insuranceRequired={insuranceRequired}
            insuranceValue={insuranceValue}
            handleInsuranceValueChange={dispatchSetInsuranceValue}
            handleInsuranceRequiredChange={dispatchSetInsuranceRequired}
            isIkeaOrg={showServiceFeatures.data?.state || false}
          />
        )}
      </Column>
    </Row>
  );

  return (
    <Wrapper>
      <HeaderWrapper>
        <HeaderText margin="1rem 0">PRODUCT INFO</HeaderText>
        {!isCreate && renderEditButtons()}
      </HeaderWrapper>
      <SectionCard padding="40px">
        <UploadLogoWrapper disabled={!isEditing}>
          <UploadLogo
            disabled={!isEditing}
            imageFile={image || null}
            setImageFile={(image: File | null) => {
              if (image) dispatchSetImage(image);
              else dispatchSetImage(null);
            }}
            uploadLogoLink={image_url}
            setUploadLogoLink={() => {
              dispatchSetImageURL("");
            }}
            removeButton={isCreate}
          />
        </UploadLogoWrapper>

        <Column flex={1}>
          {renderFirstRow()}
          {renderSecondRow()}
          {renderThirdRow()}
        </Column>
      </SectionCard>
    </Wrapper>
  );
};

export default ProductInfo;
